/**
 * Санхүү, нэхэмжлэх — прототипийн `06 Finance.dc.html`.
 *
 * ҮҮРЭГ: «мөнгө хэд орж, хэд гарав» — системийн ЦОРЫН ГАНЦ орлого ↔
 * зарлагын харьцуулалт, аялалын зардал, актлалтын алдагдал.
 *   орлого  → `order_lines.line_total_mnt` (буцаагдсаныг хассан)
 *   зарлага → `items.goods_cost_mnt` + `purchases.transport/customs/other`
 *
 * ⚠️ Хоёр тал НЭГ хугацаанд — «энэ он» (2026-09-29). Өмнө нь энэ оны
 *    орлогыг БҮХ цагийн аялалын зардалтай нэг зурваст тулгадаг байв.
 * ⚠️ Ашиг, маржин энд БАЙХГҮЙ — «Тайлан»-д шүүлтүүртэй. «Борлуулалтын
 *    ашиг» мөр нь Тайлангийн «Ашиг»-ийг давтаж, мөн зарлагын дор байхдаа
 *    «орлого − зарлага» мэт буруу уншигддаг байв.
 * ⚠️ Нуусан KPI самбар (`StatPanel`) ба түүнд л зориулсан `/reports/stock`
 *    хүсэлтийг хасав — харагддаггүй атлаа хуудсыг ачаалахыг хүлээлгэдэг,
 *    алдаа гарвал бүх хуудсыг унагадаг байв.
 * ⚠️ Нэхэмжлэх, төлбөр, салбарын зардлын хүснэгт схемд ОГТ БАЙХГҮЙ
 *    (33 хүснэгтийн аль нь ч биш). Тэр хэсгийг зохиогоогүй.
 */

import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { formatDate, formatMnt, formatMntShort, formatQty, formatSerialDisplay, reportRange } from '@boots/domain';
import { api, type Envelope } from '../lib/api';
import { useAuth } from '../lib/auth';
import { COST_ROLES, type PurchaseListMeta, type PurchaseRow, type SalesReport } from '../lib/types';
import { Empty, ErrorNote, Spinner } from '../components/ui';
import { DataTable, type Column } from '../components/data-table';
import { Meter } from '../components/meter';
import { ModuleNotice } from '../components/module-notice';

interface PurchaseList {
  data: PurchaseRow[];
  meta: PurchaseListMeta;
}

/** Мөнгө string-ээр ирдэг — зөвхөн харьцуулалт, диаграмд number болгоно. */
const num = (v: string | null | undefined) => Number(v ?? 0);

export function FinancePage() {
  const { can } = useAuth();
  const showCost = can(...COST_ROLES);
  // Орлогын тайлангийн анхдагч муж ЭНЭ ОН (УБ) — зарлагыг ч яг тэр эхлэлээс
  const yearFrom = reportRange('year', new Date())!.from.toISOString();

  const purchases = useQuery({
    queryKey: ['purchases', 'finance'],
    queryFn: () => api<PurchaseList>('/purchases?limit=100'),
    // Худалдагчид `/purchases` хаалттай — дэмий 403 авахгүй
    enabled: showCost,
  });
  // Зөвхөн нийлбэр хэрэгтэй — `meta.totals` нь шүүлтийн БҮХ мөрөөр
  const yearSpend = useQuery({
    queryKey: ['purchases', 'finance-year', yearFrom],
    queryFn: () => api<PurchaseList>(`/purchases?limit=1&from=${encodeURIComponent(yearFrom)}`),
    enabled: showCost,
  });
  const sales = useQuery({
    queryKey: ['sales-report'],
    queryFn: () => api<Envelope<SalesReport>>('/reports/sales?groupBy=day'),
  });

  const err = purchases.error ?? yearSpend.error ?? sales.error;
  if (err) return <ErrorNote>{(err as Error).message}</ErrorNote>;
  if (!sales.data || (showCost && (!purchases.data || !yearSpend.data))) return <Spinner />;

  const rows = purchases.data?.data ?? [];
  const tripTotal = purchases.data?.meta.total ?? 0;
  // Шүүлтгүй хүсэлтийн нийлбэр = БҮХ аялал (хүснэгтийн 100 мөрөөр биш)
  const allTime = purchases.data?.meta.totals;
  const totals = yearSpend.data?.meta.totals;

  const goods = num(totals?.goodsCostMnt);
  const overhead = num(totals?.overheadMnt);
  const spend = goods + overhead;

  const sale = sales.data.data.totals;
  const revenue = num(sale.revenueMnt);

  const bars = [
    { label: 'Борлуулалтын орлого', src: 'зарагдсан гутлын дүн', value: revenue },
    { label: 'Худалдан авалт (бараа)', src: 'гутлын худалдан авах үнэ', value: goods },
    { label: 'Тээвэр, гааль, бусад', src: 'аялалын зардал', value: overhead },
  ];
  const barMax = Math.max(revenue, spend, 1);

  return (
    <div className="flex flex-col gap-3">
      <ModuleNotice
        title="Нэхэмжлэх, төлбөрийн бүртгэл хараахан ороогүй"
        blockedBy={[
          'Нэхэмжлэхийн бүртгэл',
          'Төлбөрийн бүртгэл',
          'Салбарын зардлын бүртгэл',
          'Эдгээрийг нэмэх шийдвэр захиалагчаас хамаарна',
        ]}
        ready={[
          `Борлуулалтын орлого — энэ онд ${formatQty(sale.units)} гутал`,
          `${formatQty(tripTotal)} аялалын зардал`,
          'Актлалтын алдагдал',
        ]}
      />

      <div className="grid items-start gap-3 xl:grid-cols-[minmax(0,1.55fr)_minmax(0,1fr)]">
        {/* ─────────── Аялалын зардал (нэхэмжлэхийн оронд) ─────────── */}
        <section className="flex flex-col gap-2.5 rounded-card border border-hair bg-panel px-card py-4">
          <div className="flex flex-wrap items-baseline gap-3">
            <h2 className="text-base font-semibold">Аялалын зардал</h2>
            <span className="text-[12.5px] text-ink-2">
              {tripTotal > rows.length
                ? `сүүлийн ${formatQty(rows.length)} / ${formatQty(tripTotal)} аялал`
                : `${formatQty(rows.length)} аялал`}
            </span>
            <span className="text-[11.5px] text-ink-3">
              нэхэмжлэхийн бүртгэл хараахан ороогүй
            </span>
          </div>

          <DataTable
            rows={rows}
            columns={purchaseCostColumns(showCost)}
            rowKey={(p) => p.id}
            empty={
              <Empty>
                {showCost ? 'Худалдан авалт бүртгэгдээгүй' : 'Танд өртөг харах эрх алга'}
              </Empty>
            }
          />
          {showCost && allTime ? (
            <p className="num text-[12px] text-ink-2">
              Бүх аялал: бараа {formatMnt(allTime.goodsCostMnt)} · тээвэр, гааль{' '}
              {formatMnt(allTime.overheadMnt)} · нийт{' '}
              {formatMnt(String(num(allTime.goodsCostMnt) + num(allTime.overheadMnt)))}
            </p>
          ) : null}
        </section>

        {/* ─────────── Орлого, зарлага — НЭГ хугацаанд ─────────── */}
        <section className="flex flex-col gap-3 rounded-card border border-hair bg-panel px-card py-4">
          <div className="flex flex-wrap items-baseline gap-3">
            <h2 className="text-base font-semibold">Орлого, зарлага</h2>
            <span className="text-[12.5px] text-ink-2">энэ он</span>
          </div>
          {bars.map((c) => (
            <div key={c.label} className="flex flex-col gap-1.5">
              <div className="flex gap-2.5 text-[13px]">
                <span>{c.label}</span>
                <span className="num ml-auto">{showCost ? formatMntShort(String(c.value)) : '•••'}</span>
              </div>
              <Meter
                value={Math.round((c.value / barMax) * 100)}
                label={`${c.label} — эзлэх хувь`}
              />
              <span className="text-[11px] text-ink-3">эх сурвалж: {c.src}</span>
            </div>
          ))}

          <div className="flex gap-2.5 border-t border-hair pt-3 text-sm">
            <span>Нийт зарлага</span>
            <span className="num ml-auto font-semibold whitespace-nowrap">
              {showCost ? formatMnt(String(spend)) : '•••'}
            </span>
          </div>
          <p className="text-[11px] leading-[1.45] text-ink-3">
            Зарлага нь энэ онд явсан аялалын бараа, тээвэр, гааль. Салбарын
            зардал хараахан бүртгэгддэггүй. Ашиг, маржиныг{' '}
            <Link to="/reports" className="text-accent-700 hover:underline">Тайлан</Link>-гаас
            үзнэ — зарагдсан гутлын өртгөөр бодогддог.
          </p>
        </section>
      </div>

      {showCost ? <WriteOffs /> : null}
    </div>
  );
}

// ------------------------------------------------------------ Актлалт
interface WriteOffRow {
  id: string;
  /** `null` = гутлын өртөг тооцоогүй байсан. */
  lossMnt: string | null;
  note: string | null;
  writtenOffAt: string;
  reason: { labelMn: string } | null;
  approvedBy: { fullName: string } | null;
  item: { id: string; serial: string | null; size: number; model: { name: string } } | null;
}

/**
 * Актлалт — санхүүгийн алдагдал. `/write-offs` жагсаалт.
 * Актлах үйлдэл нь гутлын дэлгэрэнгүй хуудсан дээр.
 */
function WriteOffs() {
  const q = useQuery({
    queryKey: ['write-offs'],
    queryFn: () => api<{ data: WriteOffRow[]; meta: { count: number; total: number; lossMnt?: string } }>(
      '/write-offs?limit=50',
    ),
  });

  if (q.isPending) return <Spinner />;
  if (q.error) return <ErrorNote>{(q.error as Error).message}</ErrorNote>;

  const rows = q.data.data;
  const { total, lossMnt } = q.data.meta;

  return (
    <section className="flex flex-col gap-2.5 rounded-card border border-hair bg-panel px-card py-4">
      <div className="flex flex-wrap items-baseline gap-3">
        <h2 className="text-base font-semibold">Актлалт</h2>
        <span className="text-[12.5px] text-ink-2">
          нөөцөөс хассан гутал{total > rows.length ? ` · сүүлийн ${formatQty(rows.length)} / ${formatQty(total)}` : ''}
        </span>
        {/* Сервер БҮХ актлалтаар нэмсэн — харагдаж буй 50 мөрөөр биш */}
        <span className="num ml-auto text-[12.5px] text-alert-ink">
          Нийт алдагдал: {formatMnt(lossMnt ?? '0')}
        </span>
      </div>

      <DataTable
        rows={rows}
        columns={writeOffColumns()}
        rowKey={(r) => r.id}
        empty={<Empty>Актлалт бүртгэгдээгүй</Empty>}
      />
      <p className="text-[11.5px] text-ink-3">
        Актлах үйлдлийг гутлын дэлгэрэнгүй хуудаснаас хийнэ — шалтгаан ба зөвшөөрөгч заавал.
      </p>
    </section>
  );
}

function StatusChip({ status }: { status: PurchaseRow['status'] }) {
  const map: Record<PurchaseRow['status'], { label: string; cls: string }> = {
    DRAFT: { label: 'Ноорог', cls: 'bg-shell text-ink-2' },
    SYNCED: { label: 'Sync', cls: 'bg-shell text-ink-2' },
    COSTED: { label: 'Өртөг тооцсон', cls: 'bg-accent-100 text-accent-700' },
    RECEIVED: { label: 'Хүлээн авсан', cls: 'bg-accent-100 text-accent-700' },
  };
  const s = map[status];
  return (
    <span className={`rounded-full px-2.5 py-[3px] text-[11.5px] whitespace-nowrap ${s.cls}`}>
      {s.label}
    </span>
  );
}

/** Аялалын зардал — 640px-ээс доош аялал тус бүрийн карт. */
function purchaseCostColumns(showCost: boolean): Column<PurchaseRow>[] {
  const money = (v: unknown) => (showCost ? formatMnt(v) : '•••');
  return [
    {
      key: 'code',
      header: 'Дугаар',
      mobile: 'title',
      nowrap: true,
      cell: (p) => <span className="num">{p.code ?? '—'}</span>,
    },
    // Нийлүүлэгчийн кирилл нэр — боогдоно
    { key: 'supplier', header: 'Нийлүүлэгч', cell: (p) => <span className="text-pretty">{p.supplier.name}</span> },
    { key: 'goods', header: 'Бараа ₮', align: 'right', cell: (p) => money(p.goodsCostMnt) },
    { key: 'overhead', header: 'Тээвэр, гааль', align: 'right', cell: (p) => money(p.overheadTotalMnt) },
    {
      key: 'landed',
      header: 'Нийт өртөг',
      align: 'right',
      mobile: 'value',
      cell: (p) => (
        <>
          {money(p.landedCostMnt)}
          {p.costedAt ? null : <span className="ml-2 text-[11px] text-alert-ink">хуваарилаагүй</span>}
        </>
      ),
    },
    { key: 'status', header: 'Төлөв', cell: (p) => <StatusChip status={p.status} /> },
  ];
}

/** Актлалт — 640px-ээс доош гутал тус бүрийн карт. */
function writeOffColumns(): Column<WriteOffRow>[] {
  return [
    {
      key: 'item',
      header: 'Гутал',
      mobile: 'title',
      cell: (r) =>
        r.item?.serial ? (
          <Link to={`/items/${r.item.id}`} className="num text-accent-700 hover:underline">
            {formatSerialDisplay(r.item.serial)}
          </Link>
        ) : (
          <span className="text-ink-2">шошгогүй</span>
        ),
    },
    { key: 'model', header: 'Загвар', cell: (r) => <span className="text-pretty">{r.item?.model.name ?? '—'}</span> },
    { key: 'reason', header: 'Шалтгаан', cell: (r) => <span className="text-pretty">{r.reason?.labelMn ?? '—'}</span> },
    { key: 'by', header: 'Зөвшөөрсөн', cell: (r) => <span className="text-pretty">{r.approvedBy?.fullName ?? '—'}</span> },
    { key: 'loss', header: 'Алдагдал', align: 'right', mobile: 'value', cell: (r) => formatMnt(r.lossMnt) },
    { key: 'at', header: 'Огноо', align: 'right', cell: (r) => formatDate(r.writtenOffAt) },
  ];
}
